import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronRight, ChevronLeft, MapPin, Clock, CreditCard, Check, X as XIcon,
  Plane, Mountain, Home as HomeIcon, Ship, ShoppingBag, Palette, Sparkles, Building2, Footprints, Landmark, TreePine, Waves, ChefHat
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const activityIcons = {
  transfer: Plane, nature: Mountain, homestay: HomeIcon, boat: Ship,
  market: ShoppingBag, craft: Palette, ceremony: Sparkles, temple: Building2,
  trek: Footprints, culture: Landmark, city: Building2, wildlife: TreePine,
  beach: Waves, cooking: ChefHat,
};

export default function DestinationDetail() {
  const { slug } = useParams();
  const [dest, setDest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchDest = async () => {
      try {
        const res = await axios.get(`${API}/destinations/${slug}`);
        setDest(res.data);
      } catch (e) {
        console.error("Fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchDest();
  }, [slug]);

  const nextSlide = useCallback(() => {
    if (!dest?.gallery?.length) return;
    setCurrentSlide(prev => (prev + 1) % dest.gallery.length);
  }, [dest]);

  const prevSlide = useCallback(() => {
    if (!dest?.gallery?.length) return;
    setCurrentSlide(prev => (prev - 1 + dest.gallery.length) % dest.gallery.length);
  }, [dest]);

  useEffect(() => {
    if (!dest?.gallery?.length) return;
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [nextSlide, dest]);

  useEffect(() => {
    setCurrentSlide(0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2D5016] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!dest) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Destination introuvable</h1>
        <Link to="/" className="text-[#2D5016] underline font-body">Retour a l'accueil</Link>
      </div>
    );
  }

  const continentLabels = { afrique: "Afrique", asie: "Asie", "amerique-du-sud": "Amerique du Sud" };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-[#EFEBE5] px-6 md:px-12 lg:px-24 py-3" data-testid="breadcrumb">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm font-body text-[#6B6B6B]">
          <Link to="/" className="hover:text-[#2D5016]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <span>{continentLabels[dest.continent] || dest.continent}</span>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#2D5016] font-medium">{dest.name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative h-[60vh] md:h-[70vh] overflow-hidden" data-testid="destination-hero">
        <img src={dest.hero_image} alt={dest.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D5016]/80 via-[#2D5016]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-24">
          <div className="max-w-4xl">
            <p className="text-[#C8A951] font-body text-sm tracking-[0.3em] uppercase mb-2">{dest.duration} jours | A partir de {dest.price} EUR</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2">{dest.name}</h1>
            <p className="font-heading text-xl md:text-2xl text-white/90 italic">{dest.tagline}</p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24" data-testid="destination-intro">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2C2C2C] mb-8">Votre voyage</h2>
          <p className="font-body text-base md:text-lg text-[#2C2C2C]/80 leading-relaxed">{dest.description}</p>
        </div>
      </section>

      {/* Gallery Slideshow - after intro, before itinerary */}
      {dest.gallery && dest.gallery.length > 0 && (
        <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24 bg-[#EFEBE5]/50" data-testid="destination-gallery">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2C2C2C] mb-8 text-center">Galerie photos</h2>
            <div className="relative">
              {/* Main image */}
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden shadow-[0_8px_30px_-4px_rgba(45,80,22,0.15)]">
                {dest.gallery.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`${dest.name} photo ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                ))}
                {/* Navigation arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg"
                  data-testid="gallery-prev"
                >
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-[#2C2C2C]" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg"
                  data-testid="gallery-next"
                >
                  <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-[#2C2C2C]" />
                </button>
                {/* Counter */}
                <div className="absolute bottom-4 right-4 bg-[#2C2C2C]/70 backdrop-blur-sm text-white text-sm font-body px-3 py-1 rounded-full">
                  {currentSlide + 1} / {dest.gallery.length}
                </div>
              </div>
              {/* Thumbnail dots */}
              <div className="flex justify-center gap-2 mt-4">
                {dest.gallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    className={`transition-all duration-300 rounded-full ${i === currentSlide ? 'w-8 h-2.5 bg-[#2D5016]' : 'w-2.5 h-2.5 bg-[#2D5016]/30 hover:bg-[#2D5016]/50'}`}
                    data-testid={`gallery-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Itinerary */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#EFEBE5]" data-testid="destination-itinerary">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2C2C2C] mb-12">Votre circuit jour par jour</h2>

          <div className="relative">
            <div className="timeline-line" />
            <div className="space-y-8">
              {dest.itinerary.map((day, i) => {
                const IconComp = activityIcons[day.type] || MapPin;
                return (
                  <div key={i} className="relative pl-16 md:pl-20" data-testid={`itinerary-day-${day.day}`}>
                    <div className="absolute left-3 md:left-5 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-md flex items-center justify-center z-10 border-2 border-[#C8A951]">
                      <IconComp className="w-5 h-5 text-[#2D5016]" />
                    </div>
                    <div className="bg-white rounded-xl p-5 md:p-6 shadow-[0_4px_20px_-2px_rgba(45,80,22,0.06)] hover:shadow-[0_8px_30px_-4px_rgba(45,80,22,0.1)] transition-shadow">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-[#2D5016] text-[#FDF8F0] text-xs font-body font-bold px-3 py-1 rounded-full">Jour {day.day}</span>
                        {day.type === "homestay" && (
                          <span className="bg-[#C8A951]/20 text-[#A0522D] text-xs font-body font-medium px-3 py-1 rounded-full">Chez l'habitant</span>
                        )}
                      </div>
                      <h3 className="font-heading text-lg md:text-xl font-semibold text-[#2C2C2C] mb-2">{day.title}</h3>
                      <p className="font-body text-sm text-[#2C2C2C]/70 leading-relaxed">{day.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Price & Includes */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24" data-testid="destination-price">
        <div className="max-w-4xl mx-auto">
          {/* Price */}
          <div className="text-center mb-16">
            <p className="text-[#C8A951] font-body text-sm tracking-[0.3em] uppercase mb-2">Tarif par personne</p>
            <p className="font-heading text-5xl md:text-6xl font-bold text-[#2D5016]">{dest.price} EUR</p>
            <p className="font-body text-sm text-[#6B6B6B] mt-2">Base chambre double | {dest.duration} jours</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Includes */}
            <div className="bg-[#2D5016]/5 rounded-xl p-6 border border-[#2D5016]/10">
              <h3 className="font-heading text-xl font-semibold text-[#2D5016] mb-4 flex items-center gap-2">
                <Check className="w-5 h-5" /> Le prix comprend
              </h3>
              <ul className="space-y-3">
                {dest.includes.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 font-body text-sm text-[#2C2C2C]/80">
                    <Check className="w-4 h-4 text-[#2D5016] shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Excludes */}
            <div className="bg-[#A0522D]/5 rounded-xl p-6 border border-[#A0522D]/10">
              <h3 className="font-heading text-xl font-semibold text-[#A0522D] mb-4 flex items-center gap-2">
                <XIcon className="w-5 h-5" /> Le prix ne comprend pas
              </h3>
              <ul className="space-y-3">
                {dest.excludes.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 font-body text-sm text-[#2C2C2C]/80">
                    <XIcon className="w-4 h-4 text-[#A0522D] shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#2D5016]" data-testid="destination-cta">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#FDF8F0] mb-4">Envie de partir ?</h2>
          <p className="font-body text-[#FDF8F0]/80 mb-8">Demandez votre devis personnalise pour ce circuit en {dest.name}.</p>
          <Link
            to="/demande-de-devis"
            className="inline-block bg-[#C8A951] text-[#2C2C2C] rounded-full px-10 py-4 font-body font-bold text-lg hover:bg-[#C8A951]/90 transition-all shadow-xl"
            data-testid="destination-cta-devis"
          >
            Demander un devis personnalise
          </Link>
        </div>
      </section>
    </div>
  );
}
